import { observable, action, computed, configure, runInAction } from "mobx";
import { createContext } from "react";
import { IBdodJob } from "../models/bdodjob";
import { ITechnician } from "../models/technician";
import { toast } from "react-toastify";
import agent from "../api/agent";

configure({ enforceActions: "always" });

class BdodJobStore {
  @observable bdodjobRegistry = new Map();
  @observable bdodjob: IBdodJob | null = null;
  @observable bdodjobList: IBdodJob[] = new Array();
  @observable bdodjobTech: IBdodJob[] = new Array();
  @observable technicianRegistry = new Map();
  @observable technician: ITechnician | null = null;
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable target = "";

  @computed get bdodjobsByDate() {
    return Array.from(this.bdodjobRegistry.values()).sort(
      (a, b) => Date.parse(a.date) - Date.parse(b.date)
    );
  }

  @computed get techniciansByName() {
    return Array.from(this.technicianRegistry.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  @action loadBdodJobTechs = async () => {
    this.loadingInitial = true;
    try {
      const bdodjobs = await agent.BdodJobs.list();
      console.log(bdodjobs);
      runInAction("loading bdodjobs", () => {
        this.bdodjobTech = bdodjobs;
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load BdodJob List Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load BdodJob List Error");
    }
  };

  @action searchBdodJobs = async (search: {
    tech: string;
    status: string;
    jobStartDate: string;
    jobEndDate: string;
    workNo: string;
  }) => {
    if (
      search.tech === "" &&
      search.status === "" &&
      search.jobStartDate === "" &&
      search.jobEndDate === "" &&
      search.workNo === ""
    ) {
      toast.info("Please select at least one search condition");
      return;
    } else if (search.jobStartDate !== "" && search.jobEndDate === "") {
      toast.info("Please select job end date");
      return;
    } else if (search.jobEndDate !== "" && search.jobStartDate === "") {
      toast.info("Please select job start date");
      return;
    }
    const params = new URLSearchParams();
    params.append("tech", search.tech);
    params.append("status", search.status);
    params.append("jobStartDate", search.jobStartDate);
    params.append("jobEndDate", search.jobEndDate);
    params.append("workNo", search.workNo);

    this.loadingInitial = true;
    try {
      const bdodjobs = await agent.BdodJobs.search(params);
      this.bdodjobRegistry.clear();
      runInAction("loading bdodjobs", () => {
        bdodjobs.forEach((bdodjob) => {
          if (bdodjob.date !== null) bdodjob.date = bdodjob.date.substr(0, 10);
          this.bdodjobRegistry.set(bdodjob.id, bdodjob);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load BdodJob List Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load BdodJob List Error");
    }
  };

  @action loadBdodJob = async (id: string) => {
    let bdodjob = this.getBdodJob(id);
    if (bdodjob) {
      this.bdodjob = bdodjob;
      return bdodjob;
    } else {
      this.loadingInitial = true;
      try {
        const bdodjob = await agent.BdodJobs.details(id);
        runInAction("getting bdodjob", () => {
          if (bdodjob.date) {
            bdodjob.date = bdodjob.date.substr(0, 10);
          }
          this.bdodjob = bdodjob;
          this.loadingInitial = false;
        });
        return bdodjob;
      } catch (error) {
        runInAction("load BdodJob Error", () => {
          this.loadingInitial = false;
        });
        toast.error("load BdodJob Error");
      }
    }
  };

  @action clearBdodJob = () => {
    this.bdodjob = null;
    this.bdodjobList.length = 0;
  };

  getBdodJob = (id: string) => {
    return this.bdodjobRegistry.get(Number(id));
  };

  @action createBdodJob = async (bdodjob: IBdodJob[]) => {
    this.submitting = true;
    try {
      await agent.BdodJobs.create(bdodjob);
      runInAction("create Bdod Job", () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create BdodJob Error", () => {
        this.loadingInitial = false;
      });
      toast.error("create BdodJob Error");
    }
  };

  @action editBdodJob = async (bdodjob: IBdodJob) => {
    this.submitting = true;
    try {
      await agent.BdodJobs.update(bdodjob);
      runInAction("editing BdodJob", () => {
        this.bdodjobRegistry.set(bdodjob.id, bdodjob);
        this.bdodjob = bdodjob;
        this.submitting = false;
      });
    } catch (error) {
      runInAction("Edit BdodJob Error", () => {
        this.loadingInitial = false;
      });
      toast.error("Edit BdodJob  Error");
    }
  };

  @action deleteBdodJob = async (id: string) => {
    this.submitting = true;
    this.target = id;
    try {
      await agent.BdodJobs.delete(id);
      runInAction("deleting BdodJob", () => {
        this.bdodjobRegistry.delete(id);
        this.submitting = false;
        this.target = "";
      });
    } catch (error) {
      runInAction("delete BdodJob error", () => {
        this.submitting = false;
        this.target = "";
      });
      toast.error("delete BdodJob error");
      console.log(error);
    }
  };

  @action importBdodJobs = async (data: any) => {
    var rowno = 0;
    try {
      this.bdodjobList.length = 0;
      data.forEach((bdodjobs: any) => {
        rowno = rowno + 1;
        const bdodjob: IBdodJob = {
          id: "0",
          createdAt: "",
          updatedAt: "",
          workNumber: bdodjobs["WOR NO"],
          location: bdodjobs.Location,
          date: bdodjobs.Date,
          tech: bdodjobs.Tech,
          status: bdodjobs.Status,
          payment: bdodjobs.Payment,
          amount: bdodjobs.Amount,
          remark: bdodjobs.Remark,
        };
        if (bdodjob.date == null) {
          bdodjob.date = "";
        } else {
          if (!isNaN(Number(bdodjob.date))) {
            let d = new Date(
              (parseInt(bdodjob.date) - 25569) * 86400 * 1000
            );
            const pad = (s: number) => {
              return s < 10 ? "0" + s : s;
            };
            bdodjob.date = [
              d.getFullYear(),
              pad(d.getMonth() + 1),
              pad(d.getDate()),
            ].join("-");
          } else {
            let parts = bdodjob.date.split("/");
            bdodjob.date =
              parts[2] +
              "-" +
              (parseInt(parts[1]) < 10 ? "0" : "") +
              parseInt(parts[1]) +
              "-" +
              (parseInt(parts[0]) < 10 ? "0" : "") +
              parseInt(parts[0]);
          }
        }
        this.bdodjobList.push(bdodjob);
      });

      toast.success(`${this.bdodjobList.length} records successfully imported`);
    } catch (error) {
      toast.error(`import excel error, row #${rowno} has data format problem.`);
    }
  };
}

export default createContext(new BdodJobStore());
